import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Trophy, TrendingUp, FileText, Wallet, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtMoney, fmtNum } from "@/lib/format";
import RiyalSymbol from "@/components/RiyalSymbol";

export default function MyEarningsPage() {
  const { user } = useAuth();
  const { theme, dir, lang } = useI18n();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const T = lang === "ar" ? AR : EN;

  useEffect(() => {
    if (!user) return;
    api.get("/my-earnings").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [user]);

  const css = makeStyles(theme);
  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;

  if (err) return <div dir={dir} style={css.page}><div style={css.err}>{err}</div></div>;
  if (!data) return <div dir={dir} style={{ ...css.page, color: theme.textMuted }}>…</div>;

  const { bonus, income_total, office_revenue_total, applications_count, policy } = data;
  const mode = policy.calculation_mode;
  const basis = mode === "net" ? office_revenue_total : income_total;
  const target = bonus.target_amount;
  const ceiling = bonus.golden_ceiling;
  const progressToTarget = target > 0 ? Math.min(100, (basis / target) * 100) : 0;
  const progressTargetToCeiling = ceiling > target
    ? Math.min(100, Math.max(0, ((basis - target) / (ceiling - target)) * 100))
    : 0;

  return (
    <div dir={dir} style={css.page} data-testid="my-earnings-page">
      <header style={css.head}>
        <button onClick={() => navigate("/")} style={css.backBtn} data-testid="earnings-back">
          <BackIcon size={14} strokeWidth={2.4} /> {T.back}
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={css.title}>{T.title}</h1>
          <p style={css.sub}>{T.period} — {data.period} · {T.mode}: {mode === "net" ? T.mode_net : T.mode_income}</p>
        </div>
      </header>

      {/* Hero — bonus card */}
      <section style={css.hero}>
        <div style={css.heroLabel}>{T.bonus_so_far}</div>
        <div style={css.heroAmount} data-testid="earnings-total-bonus">
          <span style={{ fontFamily: "monospace", direction: "ltr" }}>{fmtMoney(bonus.total_bonus)}</span>
          <span style={css.heroCurrency}><RiyalSymbol size={20} /></span>
        </div>
        {bonus.golden_unlocked ? (
          <div style={css.heroBadge}>
            <Trophy size={16} strokeWidth={2.4} /> {T.golden_unlocked}
          </div>
        ) : basis >= target ? (
          <div style={css.heroBadge}>
            <TrendingUp size={16} strokeWidth={2.4} /> {T.target_reached}
          </div>
        ) : null}
      </section>

      {/* Progress bars */}
      <section style={css.card}>
        <h2 style={css.cardTitle}>{T.progress}</h2>

        <div style={css.progressBlock}>
          <div style={css.progressLabelRow}>
            <span style={{ fontWeight: 700 }}>{T.toward_target}</span>
            <span style={css.cellMoney}>{fmtMoney(basis)} / {fmtMoney(target)} <RiyalSymbol size={11} /></span>
          </div>
          <div style={css.progressTrack}>
            <div style={{
              width: `${progressToTarget}%`, height: "100%",
              background: basis >= target ? "linear-gradient(90deg,#16a34a,#10b981)" : theme.goldGradient,
              transition: "width 400ms ease",
            }} />
          </div>
          {basis < target && (
            <p style={css.progressHint}>
              <Sparkles size={12} style={{ verticalAlign: "middle", marginInlineEnd: 4 }} />
              {T.remaining_to_target} <strong style={{ color: theme.gold, fontFamily: "monospace" }}>{fmtMoney(bonus.remaining_to_target)}</strong> <RiyalSymbol size={11} />
            </p>
          )}
        </div>

        <div style={css.progressBlock}>
          <div style={css.progressLabelRow}>
            <span style={{ fontWeight: 700 }}>{T.toward_golden}</span>
            <span style={css.cellMoney}>{fmtMoney(Math.min(basis, ceiling))} / {fmtMoney(ceiling)} <RiyalSymbol size={11} /></span>
          </div>
          <div style={css.progressTrack}>
            <div style={{
              width: `${progressTargetToCeiling}%`, height: "100%",
              background: bonus.golden_unlocked
                ? "linear-gradient(90deg,#eab308,#f59e0b)"
                : "linear-gradient(90deg,#3b82f6,#0ea5e9)",
              transition: "width 400ms ease",
            }} />
          </div>
          {!bonus.golden_unlocked && (
            <p style={css.progressHint}>
              <Trophy size={12} style={{ verticalAlign: "middle", marginInlineEnd: 4, color: theme.gold }} />
              {T.golden_extra(fmtMoney(bonus.golden_bonus_extra))} — {T.remaining_to_ceiling} <strong style={{ color: theme.gold, fontFamily: "monospace" }}>{fmtMoney(bonus.remaining_to_ceiling)}</strong>
            </p>
          )}
        </div>
      </section>

      {/* Stats grid */}
      <section style={css.statsGrid}>
        <StatCard icon={FileText} label={T.stat_apps} value={fmtNum(applications_count)} theme={theme} testid="stat-apps" />
        <StatCard icon={Wallet} label={T.stat_income} value={<span>{fmtMoney(income_total)} <RiyalSymbol size={12} /></span>} theme={theme} testid="stat-income" />
        <StatCard icon={Wallet} label={T.stat_office} value={<span>{fmtMoney(office_revenue_total)} <RiyalSymbol size={12} /></span>} theme={theme} testid="stat-office" />
        <StatCard icon={Trophy} label={T.stat_bonus_base} value={<span>{fmtMoney(bonus.base_bonus)} <RiyalSymbol size={12} /></span>} theme={theme} testid="stat-base" />
      </section>

      {/* Policy summary */}
      <section style={css.policyBox}>
        <h3 style={css.policyTitle}>{T.policy_title}</h3>
        <ul style={css.policyList}>
          <li>{T.policy_target}: <strong>{fmtMoney(target)} <RiyalSymbol size={11} /></strong></li>
          <li>{T.policy_percent}: <strong>{bonus.bonus_percent}%</strong></li>
          <li>{T.policy_ceiling}: <strong>{fmtMoney(ceiling)} <RiyalSymbol size={11} /></strong></li>
          <li>{T.policy_extra}: <strong>+{fmtMoney(bonus.golden_bonus_extra)} <RiyalSymbol size={11} /></strong></li>
        </ul>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, theme, testid }) {
  return (
    <div style={{
      background: theme.surfaceCard, border: `1px solid ${theme.borderSoft}`,
      borderRadius: 12, padding: 16,
    }} data-testid={testid}>
      <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8, display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Icon size={12} strokeWidth={2.4} /> {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 900, color: theme.gold, fontFamily: "monospace", direction: "ltr" }}>{value}</div>
    </div>
  );
}

const AR = {
  title: "بونصي الشهري",
  back: "رجوع",
  period: "الشهر",
  mode: "أساس الحساب",
  mode_income: "الدخل الكامل",
  mode_net: "صافي المكتب",
  sar: "ر.س",
  bonus_so_far: "البونص المتراكم حالياً",
  golden_unlocked: "وصلت للسقف الذهبي — مكافأة إضافية مفتوحة",
  target_reached: "تجاوزت الهدف — البونص يتراكم",
  progress: "التقدم",
  toward_target: "نحو هدف البونص",
  toward_golden: "نحو السقف الذهبي",
  remaining_to_target: "تبقى للوصول للهدف",
  remaining_to_ceiling: "تبقى للسقف",
  golden_extra: (amount) => `مكافأة بلوغ السقف +${amount}`,
  stat_apps: "عدد المعاملات المعتمدة",
  stat_income: "إجمالي الدخل",
  stat_office: "صافي المكتب",
  stat_bonus_base: "بونص النسبة",
  policy_title: "سياسة البونص الحالية",
  policy_target: "المبلغ المستهدف",
  policy_percent: "نسبة البونص",
  policy_ceiling: "السقف الذهبي",
  policy_extra: "مكافأة بلوغ السقف",
};

const EN = {
  title: "My Monthly Bonus",
  back: "Back",
  period: "Period",
  mode: "Calculation basis",
  mode_income: "Full income",
  mode_net: "Office net",
  sar: "SAR",
  bonus_so_far: "Bonus so far",
  golden_unlocked: "Golden ceiling unlocked — extra bonus active",
  target_reached: "You crossed the target — bonus is accumulating",
  progress: "Progress",
  toward_target: "Toward bonus target",
  toward_golden: "Toward golden ceiling",
  remaining_to_target: "To reach the target",
  remaining_to_ceiling: "To reach the ceiling",
  golden_extra: (amount) => `Golden milestone +${amount}`,
  stat_apps: "Approved applications",
  stat_income: "Total income",
  stat_office: "Office net",
  stat_bonus_base: "Percentage bonus",
  policy_title: "Current bonus policy",
  policy_target: "Target amount",
  policy_percent: "Bonus percent",
  policy_ceiling: "Golden ceiling",
  policy_extra: "Golden milestone bonus",
};

const makeStyles = (theme) => ({
  page: {
    minHeight: "100vh", background: theme.bg, color: theme.text,
    fontFamily: "'Tajawal','Segoe UI',sans-serif",
    padding: "20px 16px", maxWidth: 900, margin: "0 auto",
  },
  head: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" },
  backBtn: {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
    background: "transparent", border: `1px solid ${theme.border}`,
    color: theme.text, borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 700,
    fontFamily: "inherit",
  },
  title: { color: theme.gold, fontSize: 22, fontWeight: 900, margin: 0 },
  sub: { fontSize: 12, color: theme.textMuted, margin: "4px 0 0" },
  hero: {
    background: `linear-gradient(135deg, ${theme.surfaceCard}, ${theme.surfaceAlt})`,
    border: `2px solid ${theme.borderAccent}`,
    borderRadius: 18, padding: "28px 24px",
    textAlign: "center", marginBottom: 16,
    boxShadow: "0 8px 24px rgba(212,175,55,0.08)",
  },
  heroLabel: { fontSize: 13, color: theme.textMuted, fontWeight: 700, marginBottom: 8 },
  heroAmount: {
    fontSize: 44, fontWeight: 900, color: theme.gold,
    display: "inline-flex", alignItems: "baseline", gap: 10,
  },
  heroCurrency: { fontSize: 18, color: theme.textSoft, fontWeight: 700 },
  heroBadge: {
    display: "inline-flex", alignItems: "center", gap: 6,
    marginTop: 12, padding: "6px 14px",
    background: "rgba(22,163,74,0.10)",
    color: "#16a34a",
    border: "1px solid rgba(22,163,74,0.30)",
    borderRadius: 999, fontSize: 12, fontWeight: 800,
  },
  card: {
    background: theme.surfaceCard, border: `1px solid ${theme.borderSoft}`,
    borderRadius: 14, padding: 22, marginBottom: 16,
  },
  cardTitle: { margin: "0 0 16px", fontSize: 14, fontWeight: 900, color: theme.gold },
  progressBlock: { marginBottom: 18 },
  progressLabelRow: { display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 12 },
  progressTrack: {
    width: "100%", height: 10, borderRadius: 999,
    background: theme.borderSoft, overflow: "hidden",
  },
  progressHint: { fontSize: 11, color: theme.textMuted, marginTop: 6 },
  cellMoney: { fontFamily: "monospace", fontWeight: 800, color: theme.gold, direction: "ltr" },
  statsGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 10, marginBottom: 16,
  },
  policyBox: {
    background: theme.surfaceAlt, border: `1px dashed ${theme.borderSoft}`,
    borderRadius: 12, padding: 16,
  },
  policyTitle: { fontSize: 12, fontWeight: 900, color: theme.textMuted, margin: "0 0 10px", letterSpacing: 0.4 },
  policyList: {
    listStyle: "none", padding: 0, margin: 0,
    fontSize: 13, color: theme.textSoft, lineHeight: "2",
  },
  err: { padding: 12, background: theme.errorBg, color: theme.errorText, border: `1px solid ${theme.errorBorder}`, borderRadius: 10, marginBottom: 12, fontSize: 13 },
});
